import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Frame, language, date, progress, modal, notification, color } from 'curvy';
import { User } from '@task-modules/users';
import { TaskRoutes } from '@task-utils/routes';
import { Task } from '@task-utils/types';
import { shelf_filler_translations } from './shelf-filler.trans';
import { SHELF_STATUS_UI, WORK_TYPE_SHORT, WORK_TYPE_TRANS } from './shelf-filler.shared';
import { CreateEditShelfPlanDialog } from './create-edit-shelf-plan.dialog';

export type GridChip = {
	id: number;
	status_id: Task.STATUS;
};

type ShelfView = 'month' | 'week' | 'day';

type PunjenjeEvent = {
	day: number;
	date: Date;
	status_id: Task.STATUS;
	supplier_name: string;
	location_cin: string;
	location_name: string;
	time: string | null;
	work_type_ids: Task.SHELF_WORK_TYPE[];
	is_ad_hoc: boolean;
	check_in: Task.TIMESTAMP | null;
	check_out: Task.TIMESTAMP | null;
	workers: number;
	shelf_visit_id: number;
	chip: GridChip;
};

interface ShelfLegend {
	status_id: Task.STATUS;
	icon: string;
	color: string;
	trans: string;
	selected: boolean;
}

type ShelfSupplier = { supplier_company_id: number; name: string };

@Component({
	selector: 'task-shelf-filler-calendar',
	templateUrl: './shelf-filler-calendar.component.html',
	styleUrls: ['./shelf-filler-calendar.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ShelfFillerCalendarComponent implements OnInit {
	allEvents: PunjenjeEvent[] = [];
	lastPromise: any = null;

	selectedYear: number = new Date().getUTCFullYear();
	selectedMonth: number = new Date().getUTCMonth();
	startDay = 0;
	monthDays = 30;
	isMonthAndYear = false;

	view: ShelfView = 'week';
	view_date: Date = (() => {
		const n = new Date();
		return date.set(new Date(0), { year: n.getFullYear(), month: n.getMonth(), date: n.getDate() }, true);
	})();

	months = language.get_months();
	days = language.get_weekdays_monday_start();

	legend: ShelfLegend[] = [
		Task.STATUS.CREATED,
		Task.STATUS.IN_EXECUTION,
		Task.STATUS.EXECUTED,
		Task.STATUS.UNDONE,
	].map(s => {
		const ui = SHELF_STATUS_UI[s]!;
		return { status_id: s, icon: ui.icon, color: ui.color, trans: ui.trans, selected: true };
	});

	selectedSuppliers: ShelfSupplier[] = [];
	selectedLocations: Task.Location[] = [];

	work_type_options: { id: Task.SHELF_WORK_TYPE; trans: string }[] = [
		Task.SHELF_WORK_TYPE.SHELF_STOCKING,
		Task.SHELF_WORK_TYPE.EXTRA_DISPLAY_STOCKING,
		Task.SHELF_WORK_TYPE.ASSORTMENT_CONTROL,
		Task.SHELF_WORK_TYPE.SALES_VISIT,
		Task.SHELF_WORK_TYPE.OTHER,
	].map(id => ({ id, trans: WORK_TYPE_TRANS[id] }));
	selectedTypes: { id: Task.SHELF_WORK_TYPE; trans: string }[] = [];

	is_supplier = false;
	can_add = false;
	can_export = false;
	can_no_show = false;

	refreshCount = 0;
	refresh() { this.refreshCount++; }

	ngOnInit() {
		Frame.set({
			title: language.translate('NAVROUTE.PUNJENJE'),
			visible: true,
			layout: 'top-middle',
			size: 'full',
		});
		language.load_translations(shelf_filler_translations);
		this.init();
	}

	async init() {
		this.is_supplier = await User.currentTypeIs(Task.USER_TYPE.SUPPLIER_ADMIN, Task.USER_TYPE.SUPPLIER_WORKER);
		this.can_add = await User.currentTypeIs(
			Task.USER_TYPE.SYSTEM_ADMIN, Task.USER_TYPE.ADMIN,
			Task.USER_TYPE.SHELF_COORDINATOR, Task.USER_TYPE.SUPPLIER_ADMIN);
		this.can_export = await User.currentTypeIs(Task.USER_TYPE.SYSTEM_ADMIN, Task.USER_TYPE.SHELF_COORDINATOR);
		this.can_no_show = await User.currentTypeIs(Task.USER_TYPE.SYSTEM_ADMIN, Task.USER_TYPE.SHELF_COORDINATOR);

		this.onChangeDate();
	}

	switchMonth(dir: number) {
		if (dir > 0) {
			this.selectedMonth += 1;
			if (this.selectedMonth > 11) { 
				this.selectedYear += 1; this.selectedMonth = 0;
			}
		} else {
			this.selectedMonth -= 1;
			if (this.selectedMonth < 0) { 
				this.selectedYear -= 1; this.selectedMonth = 11;
			}
		}
		this.onChangeDate();
	}

	onChangeDate() {
		const d = date.set(new Date(0), { year: this.selectedYear, month: this.selectedMonth, date: 15 } as any);
		this.startDay = date.first_day_of_month(d) - 1;
		if (this.startDay <= 0) { 
			this.startDay += 7; 
		}
		this.monthDays = date.days_in_month(d, true);

		const today = new Date();
		this.isMonthAndYear = d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();

		this.view_date = date.set(new Date(0), { year: this.selectedYear, month: this.selectedMonth, date: this.isMonthAndYear ? today.getDate() : 1 }, true);

		this.allEvents = [];
		this.getData();
	}

	set_view(v: ShelfView) {
		if (this.view === v) { 
			return; 
		}
		this.view = v;
		if (v === 'month') { 
			this.onChangeDate(); return; 
		}
		this.allEvents = [];
		this.getData();
	}

	nav(dir: number) {
		if (this.view === 'month') { 
			this.switchMonth(dir); 
			return; 
		}
		const step = this.view === 'week' ? 7 : 1;
		this.view_date = date.add(this.view_date, date.duration({ date: dir * step }));
		this.selectedYear = this.view_date.getUTCFullYear();
		this.selectedMonth = this.view_date.getUTCMonth();
		this.allEvents = [];
		this.getData();
	}

	current_range(): { start: Date; end: Date } {
		if (this.view === 'week') {
			const wd = this.week_days();
			return { start: wd[0], end: date.end_of_day(wd[6], true) };
		}
		if (this.view === 'day') {
			return { start: this.view_date, end: date.end_of_day(this.view_date, true) };
		}

		const first = date.set(new Date(0), { year: this.selectedYear, month: this.selectedMonth, date: 1 }, true);
		const next_first = date.set(new Date(0), { year: this.selectedYear, month: this.selectedMonth + 1, date: 1 }, true);
		const last = date.add(next_first, date.duration({ date: -1 }));
		return { start: first, end: date.end_of_day(last, true) };
	}

	week_days(): Date[] {
		const dow = this.view_date.getUTCDay();
		const offset = dow === 0 ? -6 : 1 - dow;
		const monday = date.add(this.view_date, date.duration({ date: offset }));
		return [0, 1, 2, 3, 4, 5, 6].map(i => date.add(monday, date.duration({ date: i })));
	}

	eventsForDate = (d: Date, _refresh?: number): PunjenjeEvent[] => {
		return this.allEvents
			.filter(ev => ev.date.getUTCFullYear() === d.getUTCFullYear()
				&& ev.date.getUTCMonth() === d.getUTCMonth()
				&& ev.date.getUTCDate() === d.getUTCDate())
			.filter(ev => this.legend.some(s => s.selected && s.status_id === ev.status_id))
			.filter(ev => !this.selectedTypes.length || ev.work_type_ids.some(t => this.selectedTypes.some(st => st.id === t)));
	};

	is_today_date(d: Date): boolean {
		const n = new Date();
		return d.getUTCFullYear() === n.getFullYear() && d.getUTCMonth() === n.getMonth() && d.getUTCDate() === n.getDate();
	}

	weekday_label(d: Date): string {
		return language.get_short_weekday(d.getUTCDay());
	}

	fmt_day(d: Date): string {
		return `${d.getUTCDate()}.${d.getUTCMonth() + 1}.`;
	}

	week_range_label(): string {
		const wd = this.week_days();
		return `${this.fmt_day(wd[0])} – ${this.fmt_day(wd[6])} ${this.view_date.getUTCFullYear()}.`;
	}

	day_label(): string {
		return `${this.weekday_label(this.view_date)} ${this.fmt_day(this.view_date)} ${this.view_date.getUTCFullYear()}.`;
	}

	// ---------- dohvat (planirani + ad-hoc posjeti za vidljivi raspon) ----------
	// dva poziva na isti /visits/search: planirani po planned_visit_date, ad-hoc po check_in_time (particija bez dupliciranja)
	async getData() {
		const range = this.current_range();

		const planned_attrs = ['-d:planned_visit_date', '+d:planned_visit_date'];
		const planned_srch: any[] = [range.start.toISOString(), range.end.toISOString()];
		const adhoc_attrs = ['-d:check_in_time', '+d:check_in_time'];
		const adhoc_srch: any[] = [range.start.toISOString(), range.end.toISOString()];

		if (this.selectedSuppliers.length) {
			const ids = this.selectedSuppliers.map(x => x.supplier_company_id);
			planned_attrs.push('U:supplier_company_id'); planned_srch.push(ids);
			adhoc_attrs.push('U:supplier_company_id'); adhoc_srch.push(ids);
		}
		if (this.selectedLocations.length) {
			const ids = this.selectedLocations.map(x => x.location_id);
			planned_attrs.push('U:location_id'); planned_srch.push(ids);
			adhoc_attrs.push('U:location_id'); adhoc_srch.push(ids);
		}

		const paginator = { page_no: 1, page_size: 1000 };
		const prom = Promise.all([
			TaskRoutes.shelf.api_shelf_visits_search({ attributes: planned_attrs, search: planned_srch }, paginator),
			TaskRoutes.shelf.api_shelf_visits_search({ attributes: adhoc_attrs, search: adhoc_srch }, paginator),
		]);
		progress.listen('load-shelf', prom);
		this.lastPromise = prom;

		let planned_res: any, adhoc_res: any;
		try {
			[planned_res, adhoc_res] = await prom;
		} catch (e: any) {
			if (this.lastPromise !== prom) { return; }
			console.error('[shelf-filler] getData failed:', e);
			notification.show({
				icon: 'error',
				color: color.Variable.warn,
				title: language.translate('ERROR'),
				message: language.translate('ERROR.GENERIC_TEXT')
			});
			this.allEvents = [];
			this.refresh();
			return;
		}
		if (this.lastPromise !== prom) { return; }

		const events: PunjenjeEvent[] = [];

		// planirani (planned=true) — pozicija po planned_visit_date
		for (const v of (planned_res.data ?? [])) {
			if (!v.planned || !v.planned_visit_date) { continue; }
			events.push(this.to_event(v, new Date(v.planned_visit_date), false));
		}

		// ad-hoc (planned=false) — pozicija po check_in_time
		for (const v of (adhoc_res.data ?? [])) {
			if (v.planned || !v.check_in_time) { continue; }
			events.push(this.to_event(v, new Date(v.check_in_time), true));
		}

		this.allEvents = events;
		this.refresh();
	}

	visit_work_types(v: Task.ShelfVisit): Task.SHELF_WORK_TYPE[] {
		if (v.work_items?.length) {
			return [...new Set(v.work_items.map(wi => wi.work_type_id))];
		}
		return v.planned_work_type_ids ?? [];
	}

	to_event(v: Task.ShelfVisit, when: Date, is_ad_hoc: boolean): PunjenjeEvent {
		return {
			day: when.getUTCDate(),
			date: when,
			status_id: v.status_id,
			supplier_name: v.supplier_name ?? '',
			location_cin: v.location_cin ?? '',
			location_name: v.location_name ?? ('#' + v.location_id),
			time: is_ad_hoc ? null : this.format_time(v.planned_visit_time),
			work_type_ids: this.visit_work_types(v),
			is_ad_hoc,
			check_in: v.check_in_time ?? null,
			check_out: v.check_out_time ?? null,
			workers: v.total_no_of_workers ?? 0,
			shelf_visit_id: v.shelf_visit_id,
			chip: { id: v.shelf_visit_id, status_id: v.status_id },
		};
	}

	format_time(t: string | null | undefined): string | null {
		return t ? t.slice(0, 5) : null;
	}

	filterStatus(l: ShelfLegend) {
		l.selected = !l.selected;
		if (!this.legend.some(s => s.selected)) { this.legend.forEach(s => s.selected = true); }
		this.refresh();
	}

	clear_filter() {
		this.selectedSuppliers = [];
		this.selectedLocations = [];
		this.selectedTypes = [];
		this.legend.forEach(s => s.selected = true);   // resetiraj i status-legendu (Planiran/U tijeku/Završeno/Nije došao)
		this.getData();
	}

	search_suppliers = async (q: string): Promise<ShelfSupplier[]> => {
		const res = await TaskRoutes.shelf.api_shelf_suppliers_get();
		const needle = (q ?? '').toLowerCase();
		return (res.data ?? []).filter((s: ShelfSupplier) => (s.name ?? '').toLowerCase().includes(needle));
	};

	format_supplier_name = (s: ShelfSupplier) => s ? s.name : '';

	search_locations = async (q: string): Promise<Task.Location[]> => {
		const res = await TaskRoutes.locations.api_location_search_visible(
			{ search: [q, Task.LOCATION_TYPE.STORE, true], attributes: ['cin+ +name', '#=:location_type_id', 'b=:nfc'] }, { page_no: 1, page_size: 50 });
		return res.data;
	};

	format_location_name = (loc: Task.Location) => loc ? `${loc.cin ?? ''} ${loc.name}`.trim() : '';

	search_visit_types = async () => this.work_type_options;

	format_visit_type = (t: { trans: string }) => t ? language.translate(t.trans) : '';

	dayEvents = (day: number): PunjenjeEvent[] => {
		return this.allEvents
			.filter(ev => ev.day === day)
			.filter(ev => this.legend.some(s => s.selected && s.status_id === ev.status_id))
			.filter(ev => !this.selectedTypes.length || ev.work_type_ids.some(t => this.selectedTypes.some(st => st.id === t)));
	};

	status_color(status: Task.STATUS): string {
		return SHELF_STATUS_UI[status]?.color ?? Task.STATUS_COLORS.GRAY;
	}
	
	status_icon(status: Task.STATUS): string {
		return SHELF_STATUS_UI[status]?.icon ?? 'radio_button_unchecked';
	}

	type_short(ids: Task.SHELF_WORK_TYPE[]): string {
		return (ids ?? []).map(id => language.translate(WORK_TYPE_SHORT[id])).join(' ');
	}

	isThisMonth(relative_day: number): boolean {
		return relative_day > 0 && relative_day <= this.monthDays + 1;
	}

	isToday(relative_day: number): boolean {
		if (!this.isThisMonth(relative_day) || !this.isMonthAndYear) { return false; }
		const dateLabel = date.set(new Date(0), { year: this.selectedYear, month: this.selectedMonth, date: relative_day }, true).getUTCDate();
		return dateLabel === new Date().getUTCDate();
	}

	getDayLabel(relative_day: number): number {
		return date.set(new Date(0), { year: this.selectedYear, month: this.selectedMonth, date: relative_day }, true).getUTCDate();
	}

	open_chip(chip: GridChip) {
		modal.open(CreateEditShelfPlanDialog, (res?: any) => {
			if (res) { 
				this.getData(); 
			}   
		}, { chip, can_edit: this.can_add, can_no_show: this.can_no_show });
	}

	new_plan() {
		modal.open(CreateEditShelfPlanDialog, (saved?: boolean) => {
			if (saved) { 
				this.getData(); 
			}
		}, { can_edit: this.can_add, can_no_show: this.can_no_show });
	}

	async export_xlsx() {
		const range = this.current_range();
		const attrs = ['-d:planned_visit_date', '+d:planned_visit_date'];
		const search: any[] = [range.start.toISOString(), range.end.toISOString()];
		const statuses = this.legend.filter(s => s.selected).map(s => s.status_id);
		if (this.selectedSuppliers.length) { attrs.push('U:supplier_company_id'); search.push(this.selectedSuppliers.map(x => x.supplier_company_id)); }
		if (this.selectedLocations.length) { attrs.push('U:location_id'); search.push(this.selectedLocations.map(x => x.location_id)); }
		if (statuses.length) { attrs.push('U:status_id'); search.push(statuses); }
		if (this.selectedTypes.length) { attrs.push('U:planned_work_type_ids'); search.push(this.selectedTypes.map(t => t.id)); }

		await TaskRoutes.shelf.api_shelf_export({ attributes: attrs, search: search });
	}

}
